<template>
  <base-modal data-int="order-cell-address-modal" @close="close">
    <template #title>
      <p class="mb-2" data-int="order-cell-address-modal-title">
        {{ title || $tc('address', 2) }}

        <b-tooltip position="is-bottom"
                   :label="addressStateConfig.label"
                   :type="addressStateConfig.tooltipColor" >
          <span class="icon"
                :class="addressStateConfig.color"
                :title="addressStateConfig.label">
            <i :class="addressStateConfig.icon"></i>
          </span>
        </b-tooltip>
      </p>
    </template>
    <template #body>
      <div class="columns">
        <transition name="fade-width">
          <div class="column">
            <address-form ref="addressFormRef"
                          :address="currentAddress"
                          :is-required="isRequired"
                          @update="onFormUpdate" />
          </div>
        </transition>
        <transition name="expand">
          <subpremise-prompt v-if="addressStatus === AddressStatus.ADD_SUBPREMISE"
                             class="column"
                             :address-status="addressStatus"
                             :current-line2="currentAddress?.line2 || ''" />
          <suggested-address v-else
                             class="column"
                             :address="currentAddress"
                             :address-status="addressStatus"
                             :session-token="props.sessionToken"
                             @suggestion-selected="onSuggestionSelected" />
        </transition>
      </div>
    </template>
    <template #actions>
      <button data-int="order-cell-address-modal-cancel-button"
              class="button"
              @click="close">
        {{ $t('cancel') }}
      </button>
      <button class="button is-primary"
              data-int="order-cell-address-modal-save-button"
              :disabled="addressStatus !== AddressStatus.VERIFIED && !isFormValid"
              @click="save">
        {{ $t('save') }}
      </button>
    </template>
  </base-modal>
</template>

<script setup lang="ts">
import AddressForm from '@/components/library/orders/cells/address/AddressForm.vue'
import BaseModal from '@/components/common/modal/BaseModal.vue'
import BTooltip from '@/buefy/src/components/tooltip/Tooltip.vue'
import i18n from '@/locales'
import SuggestedAddress from '@/components/library/orders/cells/address/SuggestedAddress.vue'
import SubpremisePrompt from '@/components/library/orders/cells/address/SubpremisePrompt.vue'
import { Address, AddressInterface } from '@/interfaces/admin/address'
import { AddressStatus } from '@/interfaces/admin/places'
import { computed, PropType, ref } from 'vue'
import { debounce } from 'lodash'
import { validate } from '@/controllers/global/places'

const props = defineProps({
  address: {
    type: Object as PropType<AddressInterface | null>,
    required: false,
    default: null
  },
  initialAddressStatus: {
    type: String as () => AddressStatus | null,
    default: null
  },
  isRequired: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: ''
  },
  sessionToken: {
    type: String,
    required: true
  }
});

const emit = defineEmits(['save', 'close']);

const addressFormRef = ref<InstanceType<typeof AddressForm> | null>(null);
const addressStatus = ref<AddressStatus | null>(props.initialAddressStatus);
const currentAddress = ref<Address|null>(!!props.address ? new Address(props.address) : null);
const isFormValid = ref(false);
const isValidating = ref(false);

/**
 * State to present different messages and subcomponents
 */
const modalState = computed(() => {
  if (!currentAddress.value) return 'incomplete';

  // Respect explicit address status states
  if (addressStatus.value === AddressStatus.ADD_SUBPREMISE) return 'missingSubpremise';
  if (addressStatus.value === AddressStatus.VERIFIED) return 'verified';

  // For UNVERIFIED or null status, check completeness
  const { line1, city, countryCode } = currentAddress.value;
  var isComplete: boolean;
  switch (countryCode) {
    case 'USA':
      isComplete = !!(line1 && city && countryCode);
      break;
    default:
      isComplete =  !!(line1 && countryCode);
  }
  return isComplete ? 'unverified' : 'incomplete';
});

const addressStateConfig = computed(() => {
  const config = {
    incomplete: {
      color: 'has-text-danger',
      icon: 'fas fa-times-circle',
      label: i18n.global.t('addressState.incomplete'),
      tooltipColor: 'is-danger'
    },
    missingSubpremise: {
      color: 'has-text-warning',
      icon: 'fas fa-exclamation-circle',
      label: i18n.global.t('addressState.missingSubpremise'),
      tooltipColor: 'is-warning'
    },
    unverified: {
      color: 'has-text-warning',
      icon: 'fas fa-exclamation-circle',
      label: i18n.global.t('addressState.unverified'),
      tooltipColor: 'is-warning'
    },
    verified: {
      color: 'has-text-success',
      icon: 'fas fa-check-circle',
      label: i18n.global.t('addressState.verified'),
      tooltipColor: 'is-success'
    }
  };
  return config[modalState.value];
});

const close = () => emit('close');

const onFormUpdate = (payload: { address: Address, isFormValid: boolean }) => {
  currentAddress.value = payload.address;
  isFormValid.value = payload.isFormValid;

  if (
    addressStatus.value == AddressStatus.UNVERIFIED &&
    payload.address.lengthWithoutCountryCode() > 3 &&
    payload.isFormValid
  ) {
    validateAddressDebounced();
  } else {
    addressStatus.value = AddressStatus.UNVERIFIED;
  }
};

const onSuggestionSelected = (addressData: AddressInterface) => {
  // Populate the form with the address data from the selected place
  if (addressFormRef.value) {
    addressFormRef.value.populateFromAddress(addressData);
    // Validate the suggested address
    currentAddress.value = new Address(addressData);
    validateAddressDebounced();
  }
};

const save = () => {
  if (isFormValid.value) {
    emit('save', {
      address: currentAddress.value,
      addressStatus: addressStatus.value
    });
    close();
  }
};

const validateAddress = async () => {
  if (!currentAddress.value) return;

  isValidating.value = true;
  try {
    const response = await validate(currentAddress.value, props.sessionToken);
    switch (response.status) {
      case AddressStatus.ADD_SUBPREMISE:
        addressStatus.value = AddressStatus.ADD_SUBPREMISE;
        break;
      case AddressStatus.VERIFIED:
        addressStatus.value = AddressStatus.VERIFIED;
        break;
      case AddressStatus.UNVERIFIED:
      default:
        addressStatus.value = AddressStatus.UNVERIFIED;
        break;
    }
  } catch (error) {
    console.error('Failed to validate address:', error);
    /**
     * Short of an API outage, unsupported addresses would be the most common error.
     * And this will default to UNVERIFIED.
     */
    addressStatus.value = AddressStatus.UNVERIFIED;
  } finally {
    isValidating.value = false;
  }
};

const validateAddressDebounced = debounce(validateAddress, 1000);
</script>

<style scoped lang="scss">
// Smooth width transition for the address form column
.fade-width-enter-active,
.fade-width-leave-active {
  transition: all 0.3s ease-in-out;
}

.fade-width-enter-from,
.fade-width-leave-to {
  opacity: 0.5;
}

// Expand transition for suggested address (complements internal transition)
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease-in-out;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
}
</style>
